import { Trans, useLingui } from "@lingui/react/macro";
import { FormProvider, useForm } from "react-hook-form";
import { useParams } from "react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { Loading } from "@medoma/components/Loading/Loading";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import { FilledButton } from "@medoma/components/Button/Button";
import CheckmarkIcon from "@medoma/components/icons/CheckmarkIcon";
import Form from "@/components/Form/Form";
import Checkbox from "@/components/Checkbox/Checkbox";
import Checkboxes from "@/components/Checkbox/Checkboxes";
import { addActivity } from "@/api/Activities";
import { knownFeatureFlagsSchema, useFeatureFlag } from "@/api/FeatureFlags";
import { saveActivityInvoicingCodes, shouldSaveInvoice } from "@/api/Invoicing";
import type { IActivityTemplate } from "@/api/Templates";
import { startOfToday } from "date-fns";
import {
  categorySchema,
  getActivityCategoryConfig,
  timeOfDaySchema,
} from "@medoma/models/activities";
import { machineFormat } from "@medoma/models/date-and-time";
import { organizationUsesInvoicingCodes } from "@medoma/models/organizations";
import type { IEmployeeCompetence } from "@medoma/models/shifts";
import { defaultRequiredCompetences } from "@medoma/models/activityRequirements";
import { useUserProfile } from "@/api/Users";
import { deducedError } from "@/Utils/ErrorUtils";
import { DescriptionInput } from "@/forms/AddActivity/DescriptionInput";
import { Scheduling } from "@/forms/AddActivity/Scheduling/Scheduling";
import { CompetencePicker } from "@/forms/AddActivity/CompetencePicker";
import { MeasurementsPicker } from "@/forms/AddActivity/MeasurementsPicker";
import { ActivityDuration } from "@/forms/AddActivity/ActivityDuration";
import { DEFAULT_ACTIVITY_DURATION_OPTION } from "@/forms/AddActivity/activityDurationModels";
import {
  activityFrequencySchema,
  customRecurrenceIntervalUnitSchema,
} from "@/forms/AddActivity/Scheduling/schedulingModels";
import { newActivityFromActivityTemplate } from "./newActivityFromActivityTemplate";
import type { CreateActivityFromTemplateFormFields } from "./createActivityFromTemplateFormSchema";

export const CreateActivityFromTemplateForm = ({
  template: { template, templateId, revisionNumber: templateRevision },
  onSuccess,
  currentEmployeeCompetence,
}: {
  template: IActivityTemplate;
  onSuccess: () => void;
  currentEmployeeCompetence: IEmployeeCompetence;
}) => {
  const { patientId } = z.object({ patientId: z.string() }).parse(useParams());
  const {
    title,
    category,
    description,
    measurements,
    requiredCompetences,
    procedureCodes,
    productCodes,
    durationOption,
    customDuration,
    customDurationUnit,
    frequency,
    recurrence,
    weekdays,
    customRecurrenceInterval,
    customRecurrenceIntervalUnit,
    timeslots,
    timeSensitivity,
    timeCategory,
    recurrencesPerDay,
    doubleStaffing,
    hidden,
  } = template;

  const { t } = useLingui();
  const queryClient = useQueryClient();
  const { organization } = useUserProfile();

  const { data: showDurationInUI } = useFeatureFlag(
    knownFeatureFlagsSchema.enum.ShowDurationInUI,
  );

  const templateProcedureCodes = procedureCodes ?? [];
  const templateProductCodes = productCodes ?? [];

  const methods = useForm<CreateActivityFromTemplateFormFields>({
    defaultValues: {
      description: description ?? "",
      measurements: measurements ?? [],
      requiredCompetences:
        requiredCompetences ??
        defaultRequiredCompetences(currentEmployeeCompetence),
      durationOption: durationOption ?? DEFAULT_ACTIVITY_DURATION_OPTION,
      customDuration,
      customDurationUnit,
      frequency: frequency ?? activityFrequencySchema.enum.oneTime,
      recurrence,
      weekdays,
      customRecurrenceInterval,
      customRecurrenceIntervalUnit:
        customRecurrenceIntervalUnit ??
        customRecurrenceIntervalUnitSchema.enum.weeks,
      timeslots,
      timeSensitivity,
      timeCategory: timeCategory ?? timeOfDaySchema.enum.Specific,
      recurrencesPerDay:
        recurrencesPerDay ??
        (timeCategory === timeOfDaySchema.enum.Specific ? "1" : undefined),
      doubleStaffing: doubleStaffing ?? false,
      hidden: hidden ?? false,
      startDate: machineFormat(startOfToday(), "yyyy-MM-dd"),
    },
  });

  const {
    formState: { errors, isDirty },
    register,
    handleSubmit,
    setError,
    reset,
    getValues,
  } = methods;

  const activityHasPatient = Boolean(patientId);

  const {
    allowsHidden,
    allowsCompetence,
    requiresCompetence,
    allowsDuration,
    allowsDoubleStaffing,
  } = getActivityCategoryConfig({
    category,
    activityHasPatient,
  });

  const { mutate, isPending } = useMutation({
    mutationFn: async (formData: CreateActivityFromTemplateFormFields) => {
      const newActivity = newActivityFromActivityTemplate({
        formData,
        title,
        category,
        templateId,
        templateRevision,
        patientId,
      });

      await addActivity(newActivity);

      if (
        shouldSaveInvoice({
          invoicingActivated: organizationUsesInvoicingCodes(organization),
          category,
          procedureCodes: templateProcedureCodes,
          productCodes: templateProductCodes,
          activityHasPatient,
        })
      ) {
        await saveActivityInvoicingCodes({
          activityId: newActivity.activityId,
          patientId,
          procedureCodes: templateProcedureCodes,
          productCodes: templateProductCodes,
        });
      }
    },
    onError: (error) => {
      setError("root.server", { message: deducedError(error) });
      reset(getValues(), {
        keepErrors: true,
        keepIsSubmitted: true,
        keepTouched: true,
        keepIsValid: true,
        keepSubmitCount: true,
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries();
      onSuccess();
    },
  });

  const validateAndSubmit = handleSubmit((formData) => {
    mutate(formData);
  });

  return (
    <FormProvider {...methods}>
      <Form onSubmit={validateAndSubmit}>
        {isPending ? (
          <Loading message={t`Lägger till aktivitet`} />
        ) : (
          <>
            {errors.root?.server?.message && !isDirty ? (
              <ErrorMessage message={errors.root.server.message} />
            ) : null}
            {category === categorySchema.enum.PatientMeasurementTask ? (
              <Form.Section>
                <MeasurementsPicker />
              </Form.Section>
            ) : null}
            <Form.Section width="half">
              <DescriptionInput showHelperText />
            </Form.Section>
            <Form.Section>
              <Scheduling />
            </Form.Section>
            {showDurationInUI && allowsDuration ? (
              <Form.Section>
                <ActivityDuration />
              </Form.Section>
            ) : null}
            {allowsCompetence ? (
              <Form.Section>
                <CompetencePicker isRequired={requiresCompetence} />
              </Form.Section>
            ) : null}
            {allowsDoubleStaffing || allowsHidden ? (
              <Form.Section>
                <Checkboxes legend={t`Övrigt`} orientation="horizontal">
                  {allowsDoubleStaffing ? (
                    <Checkbox
                      errorMessage={errors.doubleStaffing?.message}
                      label={{ text: t`Kräver dubbelbemanning` }}
                      visualStyle="framed"
                      {...register("doubleStaffing")}
                    />
                  ) : (
                    <></>
                  )}
                  {allowsHidden ? (
                    <Checkbox
                      errorMessage={errors.hidden?.message}
                      label={{ text: t`Dölj aktiviteten för patienten` }}
                      visualStyle="framed"
                      {...register("hidden")}
                    />
                  ) : (
                    <></>
                  )}
                </Checkboxes>
              </Form.Section>
            ) : null}
            <FilledButton type="submit" width="fill-container">
              <CheckmarkIcon />
              <Trans>Skapa aktiviteten</Trans>
            </FilledButton>
          </>
        )}
      </Form>
    </FormProvider>
  );
};
